import React from 'react';
import { User } from 'lucide-react';
import { formatDate, formatTime } from '../../../utils/calendarHelpers';

/**
 * Get the current date/time in a specific timezone
 */
const getCurrentTimeInTimezone = (timezone) => {
  try {
    const now = new Date();
    // Get the time string in the target timezone
    const timeStr = now.toLocaleString('en-US', { 
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    // Parse the localized string to extract hours and minutes
    const match = timeStr.match(/(\d+):(\d+)/);
    if (match) {
      return {
        hours: parseInt(match[1], 10),
        minutes: parseInt(match[2], 10),
        dateStr: now.toLocaleDateString('en-CA', { timeZone: timezone }) // YYYY-MM-DD format
      };
    }
    return { hours: now.getHours(), minutes: now.getMinutes(), dateStr: formatDate(now) };
  } catch (e) {
    const now = new Date();
    return { hours: now.getHours(), minutes: now.getMinutes(), dateStr: formatDate(now) };
  }
};

/**
 * DayView - Single day calendar view
 */
const DayView = ({
  currentDate,
  timeSlots,
  appointments,
  availability,
  calendar,
  services,
  servers,
  user,
  isAdmin,
  setSelectedSlot,
  setSelectedAvailability,
  setSelectedAppointment,
  setContextMenu,
  setShowEditAppointmentModal,
  setHoveredSlot,
  setTooltipPosition
}) => {
  // Helper to check if isAdmin is a function or boolean
  const checkIsAdmin = () => {
    return typeof isAdmin === 'function' ? isAdmin() : isAdmin;
  };
  
  // Get current time in calendar timezone (Option A)
  const calendarTimezone = calendar?.timezone || 'UTC';
  const tzTime = getCurrentTimeInTimezone(calendarTimezone);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="min-w-[400px]">
        {/* Time Slots for Single Day */}
        {timeSlots.map((hour) => {
          const dateStr = formatDate(currentDate);
          
          // Filter appointments for this hour
          // appointments have start_time in ISO format: "2026-01-09T10:00:00"
          const dayAppointments = appointments.filter(apt => {
            if (!apt.start_time) return false;
            // Extract date from ISO start_time
            const aptDate = apt.start_time.split('T')[0];
            if (aptDate !== dateStr) return false;
            // Extract hour from ISO start_time
            const aptTime = apt.start_time.split('T')[1];
            if (!aptTime) return false;
            const aptHour = parseInt(aptTime.split(':')[0]);
            return aptHour === hour;
          });
          
          // Filter availability for this hour
          // availability has date field and start_time/end_time as "HH:MM"
          const dayAvailability = availability.filter(avail => {
            if (avail.date !== dateStr) return false;
            const startHour = parseInt(avail.start_time.split(':')[0]);
            const endHour = parseInt(avail.end_time.split(':')[0]);
            return hour >= startHour && hour < endHour;
          });
          
          // Remove duplicate availability entries (same time range)
          const uniqueAvailability = dayAvailability.reduce((acc, avail) => {
            const key = `${avail.start_time}-${avail.end_time}-${avail.server_id || 'none'}`;
            if (!acc.find(a => `${a.start_time}-${a.end_time}-${a.server_id || 'none'}` === key)) {
              acc.push(avail);
            }
            return acc;
          }, []);
          
          // Use calendar timezone for current time (Option A)
          const isTodayInCalendarTz = dateStr === tzTime.dateStr;
          const isPast = (dateStr < tzTime.dateStr) || 
            (dateStr === tzTime.dateStr && hour < tzTime.hours);
          const isCurrentHour = isTodayInCalendarTz && hour === tzTime.hours;
          
          // Determine background color based on availability and hold status
          let bgColor = 'bg-white';
          if (isPast) {
            if (uniqueAvailability.length > 0 || dayAppointments.length > 0) {
              bgColor = 'bg-gray-300';
            }
          } else if (uniqueAvailability.length > 0) {
            const hasOnHoldSlot = uniqueAvailability.some(a => a.is_on_hold === true);
            if (dayAppointments.length > 0) {
              bgColor = 'bg-yellow-50';
            } else if (hasOnHoldSlot) {
              bgColor = 'bg-orange-50';
            } else {
              bgColor = 'bg-green-50';
            }
          } else if (dayAppointments.length > 0) {
            bgColor = 'bg-red-50';
          }
          
          const isBookable = uniqueAvailability.length > 0 && !uniqueAvailability.some(a => a.is_on_hold) && !isPast;
          const slotKey = `day-${dateStr}-${hour}`;
          
          // Check if user has a booking in this slot
          const customerBooking = dayAppointments.find(apt => apt.customer_id === user?.id);
          
          // Calculate time indicator position for current hour
          const timeIndicatorPosition = isCurrentHour ? (tzTime.minutes / 60) * 100 : null;
          
          return (
            <div key={hour} className="flex border-b last:border-b-0">
              {/* Time Label */}
              <div className={`w-24 p-4 border-r bg-gray-50 text-sm font-medium ${isPast ? 'text-gray-400' : 'text-gray-600'} ${isCurrentHour ? 'bg-yellow-100' : ''}`}>
                {formatTime(hour)}
              </div>
              
              {/* Slot Content */}
              <div 
                className={`relative flex-1 p-4 min-h-[80px] ${bgColor} ${!isPast ? 'cursor-pointer hover:opacity-90' : ''} transition-opacity`}
                onContextMenu={(e) => {
                  e.preventDefault();
                  const timeStr = `${hour.toString().padStart(2, '0')}:00`;
                  const hasAvail = uniqueAvailability.length > 0;
                  
                  setSelectedSlot({
                    date: dateStr,
                    time: timeStr,
                    day: currentDate,
                    hour: hour
                  });
                  
                  if (hasAvail) {
                    setSelectedAvailability(uniqueAvailability[0]);
                  } else {
                    setSelectedAvailability(null);
                  }
                  
                  if (customerBooking) {
                    setSelectedAppointment(customerBooking);
                  }
                  
                  setContextMenu({
                    x: e.clientX,
                    y: e.clientY,
                    hasAvailability: hasAvail,
                    isPastSlot: isPast,
                    isBookable: hasAvail && !uniqueAvailability[0]?.is_on_hold,
                    isCustomerBooking: !!customerBooking
                  });
                }}
                onMouseEnter={(e) => {
                  // Show tooltip on all available slots (including on-hold) - consistent with Week View
                  if (uniqueAvailability.length > 0 && !isPast) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    
                    let availableServers = [];
                    if (calendar?.multiple_servers && checkIsAdmin()) {
                      const workingServers = availability.filter(avail => {
                        if (avail.date !== dateStr || !avail.server_id) {
                          return false;
                        }
                        const serverStartHour = parseInt(avail.start_time.split(':')[0]);
                        const serverEndHour = parseInt(avail.end_time.split(':')[0]);
                        return hour >= serverStartHour && hour < serverEndHour;
                      });
                      
                      const bookedServerIds = dayAppointments
                        .filter(apt => apt.server_id)
                        .map(apt => apt.server_id);
                      
                      availableServers = [...new Set(
                        workingServers
                          .filter(ws => !bookedServerIds.includes(ws.server_id))
                          .map(ws => ws.server_id)
                      )];
                    }
                    
                    setHoveredSlot(slotKey);
                    setTooltipPosition({
                      x: rect.left,
                      y: rect.top,
                      width: rect.width,
                      appointments: [],
                      availableServerIds: availableServers,
                      slotDate: dateStr,
                      slotHour: hour
                    });
                  }
                }}
                onMouseLeave={() => {
                  setHoveredSlot(null);
                  setTooltipPosition(null);
                }}
              >
                {/* Appointments - show first as they're more important */}
                {dayAppointments.map((apt, idx) => {
                  const service = services.find(s => s.id === apt.service_id);
                  const server = servers.find(s => s.id === apt.server_id);
                  const isMyAppointment = apt.customer_id === user?.id;
                  
                  // Extract time from ISO format
                  const aptStartTime = apt.start_time?.split('T')[1]?.substring(0, 5) || '';
                  const aptEndTime = apt.end_time?.split('T')[1]?.substring(0, 5) || '';
                  
                  let tooltipText = `${apt.customer_name} - ${apt.service_name}`;
                  if (apt.server_id && server) {
                    tooltipText += ` (${server.server_name})`;
                  }
                  if (apt.notes) {
                    tooltipText += `\nNotes: ${apt.notes}`;
                  }
                  
                  return (
                    <div 
                      key={`apt-${idx}`} 
                      className={`mb-2 p-2 ${isMyAppointment ? 'bg-blue-200 border-blue-400' : 'bg-blue-100 border-blue-300'} border rounded text-sm cursor-pointer hover:bg-blue-200 transition-colors`}
                      title={tooltipText}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isMyAppointment || checkIsAdmin()) {
                          setSelectedAppointment(apt);
                          setShowEditAppointmentModal(true);
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        setHoveredSlot(null);
                        setTooltipPosition(null);
                      }}
                    >
                      <span className="font-medium text-blue-800">{apt.service_name || 'Appointment'}</span>
                      <span className="text-blue-600 ml-2">{aptStartTime} - {aptEndTime}</span>
                      {apt.customer_name && (checkIsAdmin() || isMyAppointment) && (
                        <div className="text-blue-600 text-xs mt-1">
                          <User className="w-3 h-3 inline mr-1" />
                          {apt.customer_name}
                        </div>
                      )}
                      {isMyAppointment && (
                        <div className="text-blue-700 text-xs mt-1 font-semibold">Your Booking</div>
                      )}
                    </div>
                  );
                })}
                
                {/* Availability slots - only show if slot is still available */}
                {/* For single-server calendars: don't show if there's already a booking */}
                {/* For multi-server calendars: show if there are still available servers */}
                {uniqueAvailability.map((avail, idx) => {
                  // For single-server calendars, hide availability if there's already a booking
                  if (!calendar?.multiple_servers && dayAppointments.length > 0) {
                    return null;
                  }
                  
                  if (isPast) {
                    return (
                      <div 
                        key={`avail-${idx}`} 
                        className="mb-2 p-2 bg-gray-200 border border-gray-400 rounded text-sm"
                      >
                        <span className="font-medium text-gray-500">Past slot</span>
                        <span className="text-gray-500 ml-2">{avail.start_time} - {avail.end_time}</span>
                      </div>
                    );
                  }
                  
                  const isOnHold = avail.is_on_hold === true;
                  const slotBgColor = isOnHold ? 'bg-orange-100 hover:bg-orange-200' : 'bg-green-100 hover:bg-green-200';
                  const borderColor = isOnHold ? 'border-orange-400' : 'border-green-400';
                  const textColor = isOnHold ? 'text-orange-700' : 'text-green-700';
                  const subtextColor = isOnHold ? 'text-orange-600' : 'text-green-600';
                  
                  return (
                    <div 
                      key={`avail-${idx}`} 
                      className={`mb-2 p-2 ${slotBgColor} border ${borderColor} rounded text-sm cursor-pointer transition-colors`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAvailability(avail);
                      }}
                    >
                      <span className={`font-medium ${textColor}`}>
                        {isOnHold ? '⏸ On Hold' : '✓ Available'}
                      </span>
                      <span className={`${subtextColor} ml-2`}>
                        {avail.start_time} - {avail.end_time}
                      </span>
                      {avail.server_name && (
                        <span className={`${subtextColor} ml-2`}>• {avail.server_name}</span>
                      )}
                    </div>
                  );
                })}
                
                {uniqueAvailability.length === 0 && dayAppointments.length === 0 && !isPast && (
                  <div className="text-gray-400 text-sm italic">
                    {checkIsAdmin() ? 'Right-click to add availability or appointment' : 'No availability'}
                  </div>
                )}
                
                {/* Current Time Indicator (Option A: uses calendar timezone) */}
                {isCurrentHour && timeIndicatorPosition !== null && (
                  <div 
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: `${timeIndicatorPosition}%` }}
                  >
                    <div className="h-0.5 bg-blue-500 relative">
                      <div className="absolute left-0 w-2 h-2 bg-blue-500 rounded-full -mt-0.5"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DayView;
